import { useCallback, useState } from "react";

const isBrowser = () => typeof window !== "undefined";

export const useLocalStorage = <T,>(keyName: string, defaultValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!isBrowser()) {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(keyName);
      if (item === null) {
        window.localStorage.setItem(keyName, JSON.stringify(defaultValue));
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((previous: T) => T)) => {
      setStoredValue((current) => {
        const valueToStore = value instanceof Function ? value(current) : value;

        if (isBrowser()) {
          try {
            window.localStorage.setItem(keyName, JSON.stringify(valueToStore));
          } catch (error) {
            // Swallow storage write errors (e.g., private mode quota exceeded)
          }
        }

        return valueToStore;
      });
    },
    [keyName]
  );

  return [storedValue, setValue] as const;
};
